import type { Cell, Worksheet } from "exceljs";
import type { PrintoutShowAdminsConfig } from "../types";

// "Very small" spacer row separating the admins block from the first section.
const ADMIN_BLOCK_SPACER_ROW_HEIGHT = 6;

// Default layout knobs used when the config doesn't supply them (back-compat for
// rows written before these fields existed).
const DEFAULT_START_COLUMN = 1;
const DEFAULT_PAIR_SPACING = 1;

// Each pair occupies two adjacent cells (Role, Name); pairs are separated by `pairSpacing`
// empty columns. Stride = 2 + pairSpacing.
const CELLS_PER_PAIR = 2;

interface RenderAdminsBlockArgs {
	worksheet: Worksheet;
	startRow: number;
	config: PrintoutShowAdminsConfig | undefined;
	totalColumns: number;
}

/**
 * Emit the admin name/role pairs starting at `startRow` and return the next available data row.
 *
 * No-op (returns `startRow` unchanged) when disabled, missing, or the admins list is empty.
 *
 * Layout: pairs flow left-to-right starting at `config.startColumn`. Each pair takes two cells
 * (Role, Name) with `config.pairSpacing` empty columns between adjacent pairs. When the next pair
 * would overflow the sheet's column count, the layout wraps to a new row. After the final admin
 * row, a single short spacer row is inserted before the renderer continues with sections.
 */
export const renderAdminsBlock = ({ worksheet, startRow, config, totalColumns }: RenderAdminsBlockArgs): number => {
	if (!config?.enabled) return startRow;
	const admins = config.admins.filter((a) => a.name.trim() !== "" || a.role.trim() !== "");
	if (admins.length === 0) return startRow;

	const startCol = Math.min(Math.max(1, config.startColumn ?? DEFAULT_START_COLUMN), totalColumns);
	const pairSpacing = Math.max(0, config.pairSpacing ?? DEFAULT_PAIR_SPACING);
	const stride = CELLS_PER_PAIR + pairSpacing;

	// Usable columns to the right of `startCol`, inclusive. `+ pairSpacing` lets the final pair
	// in a row sit flush against the sheet edge — we don't need a trailing spacer after it.
	const usableColumns = Math.max(CELLS_PER_PAIR, totalColumns - startCol + 1);
	const pairsPerRow = Math.max(1, Math.floor((usableColumns + pairSpacing) / stride));

	let row = startRow;
	for (let i = 0; i < admins.length; i += pairsPerRow) {
		const slice = admins.slice(i, i + pairsPerRow);
		slice.forEach((admin, idx) => {
			const roleCol = startCol + idx * stride;
			const nameCol = roleCol + 1;
			const nameCell: Cell = worksheet.getCell(row, nameCol);
			const roleCell: Cell = worksheet.getCell(row, roleCol);
			nameCell.value = admin.name;
			roleCell.value = `${admin.role}:`;
			nameCell.font = { bold: true };
			roleCell.font = { bold: true, underline: "single" };
		});
		row += 1;
	}

	worksheet.getRow(row).height = ADMIN_BLOCK_SPACER_ROW_HEIGHT;
	return row + 1;
};
